import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Configure axios defaults
const getApiBaseUrl = () => {
  // In production, use relative URL so it works with current domain
  if (import.meta.env.PROD) {
    return '/api'
  }
  // In development, force backend port to ensure correct routing
  return 'http://localhost:5000/api'
}

console.log('🔗 API Base URL:', getApiBaseUrl()) // Debug log
axios.defaults.baseURL = getApiBaseUrl()
axios.defaults.withCredentials = true

// Request interceptor to add auth token
axios.interceptors.request.use(
  (config) => {
    // Skip adding token for login and refresh requests
    const isLoginRequest = config.url?.includes('/auth/login') || config.url?.endsWith('/login')
    const isRefreshRequest = config.url?.includes('/auth/refresh') || config.url?.endsWith('/refresh')
    
    if (!isLoginRequest && !isRefreshRequest) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // Skip token refresh for login requests to prevent cascading errors
    const isLoginRequest = original.url?.includes('/auth/login') || original.url?.endsWith('/login')
    const isRefreshRequest = original.url?.includes('/auth/refresh') || original.url?.endsWith('/refresh')
    
    if (error.response?.status === 401 && !original._retry && !isLoginRequest && !isRefreshRequest) {
      original._retry = true

      try {
        // Check if user opted for stay signed in
        const staySignedIn = localStorage.getItem('staySignedIn') === 'true'
        console.log('Attempting token refresh for staySignedIn:', staySignedIn)
        
        const response = await axios.post('/auth/refresh', { staySignedIn })
        const { token } = response.data
        
        console.log('Token refresh successful, updating storage')
        
        // Store in the appropriate storage based on user's stay signed in preference
        if (staySignedIn) {
          localStorage.setItem('token', token)
          // Clear any session token
          sessionStorage.removeItem('token')
        } else {
          sessionStorage.setItem('token', token)
          // Clear any persistent token
          localStorage.removeItem('token')
        }
        
        // Update the original request with the new token
        original.headers.Authorization = `Bearer ${token}`
        console.log('Retrying original request with new token')
        return axios(original)
      } catch (refreshError) {
        console.log('Token refresh failed:', refreshError.response?.data || refreshError.message)
        
        // Clear all tokens and user data
        localStorage.removeItem('token')
        sessionStorage.removeItem('token')
        localStorage.removeItem('staySignedIn')
        
        // Show a user-friendly message only if not on login page
        if (!window.location.pathname.includes('/login')) {
          toast.error('Your session has expired. Please log in again.')
        }
        
        // Force redirect immediately - don't wait, but only if not already on login page
        if (!window.location.pathname.includes('/login')) {
          console.log('Redirecting to login page')
          window.location.href = '/login'
        }
        
        return Promise.reject(refreshError)
      }
    }

    // Handle other types of 401 errors (like invalid tokens on first request)
    if (error.response?.status === 401 && original._retry && !isLoginRequest) {
      console.log('Authentication failed after token refresh attempt')
      
      // Clear all tokens
      localStorage.removeItem('token')
      sessionStorage.removeItem('token')
      localStorage.removeItem('staySignedIn')
      
      // Show error only if not on login page
      if (!window.location.pathname.includes('/login')) {
        toast.error('Authentication failed. Please log in again.')
        
        setTimeout(() => {
          window.location.href = '/login'
        }, 1000)
      }
    }

    return Promise.reject(error)
  }
)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (token) {
        try {
          const response = await axios.get('/auth/verify')
          setUser(response.data.user)
        } catch (error) {
          localStorage.removeItem('token')
          sessionStorage.removeItem('token')
          localStorage.removeItem('staySignedIn')
          console.error('Token verification failed:', error)
        }
      }
      setLoading(false)
    }

    initAuth()
  }, [])

  // Separate effect for periodic token validation to avoid infinite loops
  useEffect(() => {
    const staySignedIn = localStorage.getItem('staySignedIn') === 'true'
    if (user) {
      const interval = setInterval(async () => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token')
        if (token) {
          try {
            await axios.get('/auth/verify')
          } catch (error) {
            // The response interceptor will handle the token refresh/logout
            console.log('Background token validation failed, response interceptor will handle it')
          }
        }
      }, 30 * 60 * 1000) // Check every 30 minutes (less frequent since tokens last longer)

      return () => clearInterval(interval)
    }
  }, [user?.id]) // Only depend on user ID to avoid constant re-runs

  const login = async (email, password, staySignedIn = false, totpToken = null) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
        staySignedIn,
        ...(totpToken ? { totpToken } : {}),
      })

      // Server asks for 2FA code before issuing token
      if (response.data.requires2FA) {
        return { success: false, requires2FA: true }
      }

      // Server requires MFA setup before completing login
      if (response.data.requiresMfaSetup) {
        return { success: false, requiresMfaSetup: true, setupToken: response.data.setupToken }
      }

      const { token, user } = response.data

      // Store token based on "Stay signed in" preference
      if (staySignedIn) {
        localStorage.setItem('token', token)
        localStorage.setItem('staySignedIn', 'true')
        sessionStorage.removeItem('token')
      } else {
        sessionStorage.setItem('token', token)
        localStorage.removeItem('token')
        localStorage.removeItem('staySignedIn')
      }

      setUser(user)

      toast.success(`Welcome back, ${user.name}!`)
      return { success: true, user }
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed'
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await axios.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('token')
      sessionStorage.removeItem('token')
      localStorage.removeItem('staySignedIn')
      // Keep the user's "Stay signed in" preference for future logins
      // localStorage.removeItem('rememberStaySignedIn') - commented out to preserve preference
      setUser(null)
      toast.success('Logged out successfully')
    }
  }

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData)
      toast.success('User registered successfully')
      return { success: true, data: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/users/profile', profileData)
      setUser(prev => ({ ...prev, ...profileData }))
      toast.success('Profile updated successfully')
      return { success: true, data: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.put('/auth/password', {
        currentPassword,
        newPassword
      })
      toast.success('Password changed successfully')
      return { success: true, data: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/verify')
      setUser(response.data.user)
      return response.data.user
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      return null
    }
  }

  // Complete login after forced MFA setup — called by Login.jsx after /2fa/verify returns loginComplete
  const completeForcedMfaLogin = (token, userData) => {
    sessionStorage.setItem('token', token)
    localStorage.removeItem('token')
    localStorage.removeItem('staySignedIn')
    setUser(userData)
    toast.success(`Welcome back, ${userData.name}!`)
  }

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    refreshUser,
    completeForcedMfaLogin,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client',
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}